import React, { useState } from "react";
import { ThumbsUp, Reply, ShieldCheck } from "lucide-react";

function Avatar({ picture, name }) {
  return (
    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-semibold text-white overflow-hidden shrink-0">
      {picture ? <img src={picture} alt={name} loading="lazy" className="w-full h-full object-cover" /> : name?.[0]}
    </div>
  );
}

function MessageRow({ d, onLike, onReply, isReply }) {
  return (
    <div data-testid={`discussion-message-${d.id}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Avatar picture={d.user_picture} name={d.user_name} />
        <span className="text-xs font-semibold text-white">{d.user_name}</span>
        {d.is_mentor && (
          <span className="flex items-center gap-1 bg-indigo-400/10 border border-indigo-400/30 rounded-full px-1.5 py-0.5 text-[9px] font-bold text-indigo-400" data-testid={`mentor-badge-${d.id}`}>
            <ShieldCheck className="w-2.5 h-2.5" /> MENTOR
          </span>
        )}
      </div>
      <p className="text-sm text-slate-300 pl-8">{d.message}</p>
      <div className="pl-8 mt-2 flex items-center gap-4">
        <button onClick={() => onLike(d.id)} data-testid={`discussion-like-button-${d.id}`} className={`flex items-center gap-1 text-xs ${d.liked_by_me ? "text-indigo-400" : "text-slate-400 hover:text-slate-300"}`}>
          <ThumbsUp className="w-3.5 h-3.5" /> {d.like_count}
        </button>
        {!isReply && (
          <button onClick={() => onReply(d.id)} data-testid={`discussion-reply-button-${d.id}`} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-300">
            <Reply className="w-3.5 h-3.5" /> Reply
          </button>
        )}
      </div>
    </div>
  );
}

export default function DiscussionThread({ discussions, onLike, onReply }) {
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const submitReply = (parentId) => {
    if (!replyText.trim()) return;
    onReply(parentId, replyText);
    setReplyText("");
    setReplyingTo(null);
  };

  return (
    <div className="space-y-3">
      {discussions.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No discussions yet. Be the first to comment!</p>}
      {discussions.map((d) => (
        <div key={d.id} className="glass-panel rounded-xl p-4">
          <MessageRow d={d} onLike={onLike} onReply={(id) => setReplyingTo(replyingTo === id ? null : id)} isReply={false} />

          {d.replies?.length > 0 && (
            <div className="mt-4 ml-8 pl-4 border-l border-white/10 space-y-4">
              {d.replies.map((r) => (
                <MessageRow key={r.id} d={r} onLike={onLike} isReply />
              ))}
            </div>
          )}

          {replyingTo === d.id && (
            <div className="mt-3 ml-8 flex gap-2">
              <input
                value={replyText} onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitReply(d.id)}
                placeholder="Write a reply..." data-testid={`discussion-reply-input-${d.id}`}
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              />
              <button onClick={() => submitReply(d.id)} data-testid={`discussion-reply-submit-${d.id}`} className="px-3 py-2 bg-primary hover:bg-primary/90 rounded-lg text-xs text-white font-semibold">
                Reply
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
